import type { members } from '@wix/members';
import DataLoader from 'dataloader';

import { POLL_TYPE } from './../../ricos-content';
import type { GetWixClientPromise } from './../../_shared/request-service-provider/use-get-wix-client-promise';
import type { PollsMember } from './../../plugin-social-polls';

import { PollsService } from './PollsService';

const isSSR = () => typeof window === 'undefined';

const getMembersModulePromise = () => {
  return import(/* webpackChunkName: "wix-ricos-members" */ '@wix/members').then(
    module => module.members
  );
};

const fetchMembers = (
  membersIds: string[],
  getWixClientPromise: GetWixClientPromise
): Promise<PollsMember[]> =>
  Promise.all([getWixClientPromise(), getMembersModulePromise()]).then(([wixClient, members]) =>
    wixClient
      .use(members)
      .queryMembers()
      .in('_id', membersIds)
      .find()
      .then(res => res.items)
      .catch(e => {
        // eslint-disable-next-line no-console
        console.error('[pollsStrategy] fetchMembers failed. error:', e);
        return [];
      })
      .then(normalizeMembers)
  );

// caching per module will solve fetching of members per each viewer
const membersCache = new Map<string, Promise<PollsMember | Error>>();

function createMembersLoader(loadMembers: (memberIds: string[]) => Promise<PollsMember[]>) {
  return new DataLoader<string, PollsMember | Error>(
    (memberIds: readonly string[]) => {
      return loadMembers(memberIds as string[]).then(memberList =>
        memberIds.map(
          id => memberList.find(m => m.siteMemberId === id) || new Error('Member not found')
        )
      );
    },
    { cacheMap: membersCache }
  );
}

const normalizeMembers = (rawMembers: members.Member[]): PollsMember[] =>
  rawMembers?.map?.(member => {
    return {
      siteMemberId: member._id ?? undefined,
      imageUrl: member.profile?.photo?.url ?? undefined,
      name: {
        nick: member.profile?.nickname ?? undefined,
      },
    };
  });

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function pollsStrategy(
  instance: string | undefined,
  getWixClientPromise: GetWixClientPromise
) {
  if (!instance) {
    return {};
  }
  // TODO: trigger getSiteMembers & fetch inside polls and wait for response

  const getSiteMembers = isSSR()
    ? undefined
    : (() => {
        const loader = createMembersLoader(async (memberIds: string[]) => {
          const members = await fetchMembers(memberIds, getWixClientPromise);
          return memberIds.map(
            id =>
              members.find(m => m.siteMemberId === id) || {
                // We fallback to private member if member not found, or we have fetch error for now.
                siteMemberId: id,
              }
          );
        });
        return loader.loadMany.bind(loader);
      })();
  return {
    config: {
      [POLL_TYPE]: {
        pollServiceApi: new PollsService(getWixClientPromise),
        siteToken: instance,
        getSiteMembers,
      },
    },
  };
}
